import {TimeDelta} from '@wandb/weave/common/util/time';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {IconLightbulbInfo} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import numeral from 'numeral';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {matchPath, useParams} from 'react-router';
import {useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Container, Grid, SearchProps, Tab} from 'semantic-ui-react';

import {BannerBoldText, BannerFlexWrapper} from '../components/Banner';
import ProjectsTable from '../components/ProjectsTableComputeHour';
import {AccountSelectorContext} from '../components/Search/SearchNav/AccountSelectorContextProvider';
import StorageConfirmDelete from '../components/StorageConfirmDelete';
import StorageFileBrowser from '../components/StorageFileBrowser';
import {envIsLocal, envIsPublicCloud} from '../config';
import {
  FolderMetadata,
  useArtifactCollectionStorageTreeQuery,
  useEntityStorageTreeQuery,
  useRunFilesAndFoldersLazyQuery,
  useTeamOrganizationQuery,
} from '../generated/graphql';
import {
  STORAGE_EXPLORER_COMPUTE_HOURS_PATH,
  STORAGE_EXPLORER_TRACKED_PATH,
} from '../routes/paths';
import {MAX_NUM_FILES, MAX_STORAGE_TREE_NODES} from '../util/constants';
import * as Urls from '../util/urls';
import {TrackedHourUsageCard} from './Billing/AccountSettings/BillingTab/UsageCard/TrackedHourUsageCard';
import {LimitedResourceType} from './Billing/AccountSettings/BillingTab/UsageCard/UsageCardLimitedResource';
import {UsageCardWithCachedView} from './Billing/AccountSettings/BillingTab/UsageCard/UsageCardWithCachedView';
import {
  File,
  Node,
  StorageTableContext,
  StorageTableContextProvider,
  StorageTableContextState,
  StorageTableDispatchContext,
  TreeNode,
  TreeNodeType,
} from './Billing/AccountSettings/ManageUsagePage/StorageTableContextProvider';
import {NoAccessPage} from './NoAccessPage';
import * as S from './StorageExplorer.styles';

type StorageExplorerProps = {
  isStorageTable?: boolean;
  teamIsChecked?: boolean;
};

export const HIDDEN_FILES = [/^wandb-metadata\.json$/, /^artifact\/\d+\/.+$/];

export function fileHidden(name: string) {
  return HIDDEN_FILES.some(regex => regex.test(name));
}

type Optional<T, K extends keyof T> = Omit<T, K> & Partial<T>;
function nodeFactory(
  overrides: Optional<Node, 'files' | 'subdirectories'>
): Node {
  return Object.assign(
    {
      files: [],
      subdirectories: {},
      size: 0,
      searchable: false,
      selectable: true,
      selected: false,
      fileCount: 0,
    },
    overrides
  );
}

// Takes a flat array of file objects and converts it into a nested object, based on filenames
export function makeFileTree(
  filesArray: File[],
  foldersArray: FolderMetadata[],
  folderPath: string | undefined,
  opts: {
    selectedIds: Set<string>;
    selectedPaths: string[];
    runId?: string;
    selected?: boolean;
  }
): Node {
  const fileTree = nodeFactory({
    selected: opts.selected === true,
    id: opts.runId,
    type: 'run',
  });
  if (foldersArray != null && foldersArray.length > 0) {
    foldersArray.forEach(folder => {
      const currentFolder = fileTree;
      const folderName = folder.name;
      let folderId = folderName; // Root Directory
      if (folderPath?.length !== 0) {
        folderId = folderPath + '/' + folderName;
      } else if (folderId === 'artifact') {
        // exclude root directory 'artifact' folder
        return;
      }
      let selected = fileTree.selected;
      const pathSelected =
        selected || opts.selectedPaths.some(p => folderId === p);
      let disabled = pathSelected;
      if (!selected && pathSelected) {
        disabled = false;
        selected = true;
      }
      currentFolder.childSelected = selected;
      if (!currentFolder.subdirectories[folderName]) {
        currentFolder.subdirectories[folderName] = nodeFactory({
          selected,
          disabled,
          childSelected: selected,
          name: folderName,
          size: folder.size,
          fileCount: folder.numberOfFiles,
          type: 'folder',
          id: folderId,
        });
        const subFolder = currentFolder.subdirectories[folderName];
        const summary =
          subFolder.fileCount +
          (subFolder.fileCount === 1 ? ' file' : ' files');
        subFolder.summary = summary;
      }
    });
    filesArray.forEach(file => {
      if (fileHidden(file.name)) {
        return;
      }
      file.selected = fileTree.selected || opts.selectedIds.has(file.id);
      file.disabled = fileTree.selected;
      fileTree.files.push(file);
    });
    return fileTree;
  }

  filesArray.forEach(file => {
    if (fileHidden(file.name)) {
      return;
    }
    let currentFolder = fileTree;
    // 'media/images/image01.jpg' => ['media','images','image01.jpg']
    const path = file.name.split('/');
    let selected = fileTree.selected;
    const currentPath: string[] = [];
    while (path.length > 1) {
      // The following is safe to do because we made sure path had elems in the loop condition.
      const folderName = path.shift()!;
      currentPath.push(folderName);
      const pathSelected =
        selected || opts.selectedPaths.some(p => currentPath.join('/') === p);
      let disabled = pathSelected;
      // Don't disable the first folder that's selected
      if (!selected && pathSelected) {
        disabled = false;
        selected = true;
      }
      currentFolder.childSelected = selected;
      // create subfolder if it doesn't already exist
      if (!currentFolder.subdirectories[folderName]) {
        currentFolder.subdirectories[folderName] = nodeFactory({
          selected,
          disabled,
          childSelected: selected,
          name: folderName,
        });
      }
      currentFolder.size! += file.sizeBytes;
      currentFolder.fileCount! += 1;
      const subFolder = currentFolder.subdirectories[folderName];
      currentFolder = subFolder;
    }
    // if we've come to the last item in the path, add this file object to the current folder
    currentFolder.size! += file.sizeBytes;
    currentFolder.fileCount! += 1;
    file.selected =
      fileTree.selected ||
      currentFolder.selected ||
      opts.selectedIds.has(file.id);
    file.disabled = fileTree.selected;
    currentFolder.files.push(file);
    // Lets add some nice summaries.
    let summary = '';
    const subFolderCount = Object.keys(currentFolder.subdirectories).length;
    if (subFolderCount > 0) {
      summary +=
        subFolderCount + (subFolderCount === 1 ? ' subfolder' : ' subfolders');
    }
    if (currentFolder.fileCount! > 0) {
      if (subFolderCount > 0) {
        summary += ', ';
      }
      summary +=
        currentFolder.fileCount +
        (currentFolder.fileCount === 1 ? ' file' : ' files');
    }
    currentFolder.summary = summary;
  });

  return fileTree;
}

export function getSelectionSummary(state: StorageTableContextState): string {
  const nodeTypes = Array.from(state.selectedIds).map((id: string) => {
    return state.leafMap[id].type;
  });
  const counts = nodeTypes.reduce(
    (counter: {[id: string]: number}, nodeType: TreeNodeType) => {
      counter[nodeType] = counter[nodeType] || 0;
      counter[nodeType]++;
      return counter;
    },
    {} as {[id: string]: number}
  );
  const keys = Object.keys(counts);
  let summary = keys
    .map(
      (c, i) =>
        `${counts[c]} ${c.replace('_', ' ')}${counts[c] > 1 ? 's' : ''}${
          keys.length === 1 || i === keys.length - 1
            ? ' '
            : i === keys.length - 2
              ? ' and '
              : ', '
        }`
    )
    .join('');
  summary += ` selected (${numeral(state.selectedBytes).format('0.0b')})`;
  return summary;
}

// formats run, artifact, and file to read sanely
function pluralize(singular: string, count: number): string {
  if (count === 1) {
    return `${count} ${singular}`;
  } else if (count === MAX_STORAGE_TREE_NODES) {
    return `${count}+ ${singular}s`;
  } else {
    return `${count} ${singular}s`;
  }
}

export type StorageExplorerURLParams = {
  entityName: string;
  filePath?: string;
  orgName: string;
};

export const StorageExplorer: React.FC<StorageExplorerProps> = React.memo(
  ({isStorageTable = false, teamIsChecked = false}) => {
    const history = useHistory();
    const {entityName, filePath} = useParams<StorageExplorerURLParams>();
    const isTrackedPath =
      matchPath(window.location.pathname, {
        path: STORAGE_EXPLORER_TRACKED_PATH,
        exact: true,
      }) != null && envIsLocal;

    const isComputeHourPath =
      matchPath(window.location.pathname, {
        path: STORAGE_EXPLORER_COMPUTE_HOURS_PATH,
        exact: true,
      }) != null || envIsPublicCloud;

    const state = useContext(StorageTableContext);
    const dispatch = useContext(StorageTableDispatchContext);

    const [projectName, setProjectName] = useState<string | undefined>(
      undefined
    );

    const [runName, setRunName] = useState<string | undefined>(undefined);

    const [runFileFolderPath, setRunFileFolderPath] = useState<
      string | undefined
    >(undefined);

    const [getRunFiles, {data: runData, loading: runLoading, error: runError}] =
      useRunFilesAndFoldersLazyQuery({fetchPolicy: 'cache-and-network'});
    const [artifactName, setArtifactName] = useState<string | undefined>(
      undefined
    );
    const [artifactCollectionID, setArtifactCollectionID] = useState<
      string | undefined
    >(undefined);
    const [artifactTypeName, setArtifactTypeName] = useState<
      string | undefined
    >(undefined);
    const [typeName, setTypeName] = useState<string | undefined>(undefined);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [files, setFiles] = useState<File[]>([]);
    const [folders, setFolders] = useState<FolderMetadata[]>([]);
    const [searchResults, setSearchResults] = useState<Node | undefined>(
      undefined
    );
    const [usedBytes, setUsedBytes] = useState<number>(0);
    const [query, setQuery] = useState<string | undefined>('');

    const {
      loading,
      data,
      refetch,
      error: storageTreeError,
    } = useEntityStorageTreeQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        entityName,
        enableReferenceTracking: isTrackedPath,
        query,
        cached: false,
      },
    });

    const resetQuery = useCallback(() => {
      setQuery('');
    }, []);

    // for some reason the whole storage tree query doesn't have auth testing in backend
    // but the nested "storage tree" field within the query does, so data always exist
    // with no error. However, if user doesn't have permissions, the storage tree
    // field will be empty
    const userHasPermissions = data?.entity?.storageTree != null;

    const teamOrganizationQuery = useTeamOrganizationQuery({
      variables: {entityName},
    });

    const isTeam = teamOrganizationQuery.data?.entity?.isTeam;

    const showStoragePercentage = !isComputeHourPath || isTeam;

    const orgName = teamOrganizationQuery.data?.entity?.organization?.name;

    const computeHours = teamOrganizationQuery.data?.entity?.computeHours || 0;

    const pathParts = useMemo(() => {
      const parts = filePath ? filePath.split('/') : [];
      // Automatically navigate to artifacts in tracking mode
      if (isTrackedPath && parts.length > 0) {
        if (parts.length === 1 || parts[1] !== 'artifacts') {
          parts.splice(1, 0, 'artifacts');
        }
      }
      return parts;
    }, [filePath, isTrackedPath]);

    const setFilePath = useCallback(
      (newPathArray: string[]) => {
        history.push(
          Urls.storageFilePath(
            orgName ?? entityName,
            entityName,
            newPathArray.join('/'),
            isStorageTable,
            isTrackedPath
          )
        );
        if (isStorageTable) {
          dispatch({type: 'clear'});
        }
      },
      [entityName, isTrackedPath, orgName, isStorageTable, dispatch, history]
    );

    // pathParts is project, type, run_or_artifact_type, file_path_or_artifact_sequence
    useEffect(() => {
      // Parse the browser path and set state accordingly
      if (pathParts.length > 0) {
        setProjectName(pathParts[0]);
      } else {
        setFiles([]);
        setSearchResults(undefined);
        setProjectName(undefined);
      }
      if (pathParts.length > 1) {
        setTypeName(pathParts[1]);
      } else {
        setTypeName(undefined);
      }
      if (pathParts.length === 3 && pathParts[1] === 'runs') {
        setRunName(pathParts[2]);
        setRunFileFolderPath(String());
      } else if (pathParts.length > 3 && pathParts[1] === 'runs') {
        const currentRunsFilePath = pathParts
          .slice(3, pathParts.length)
          .join('/');
        setRunFileFolderPath(currentRunsFilePath);
      } else {
        setRunName(undefined);
        setRunFileFolderPath(undefined);
      }
      if (pathParts.length > 2 && pathParts[1] === 'artifacts') {
        setArtifactTypeName(pathParts[2]);
        if (pathParts.length > 3) {
          setArtifactName(pathParts[3]);
        } else {
          setArtifactName(undefined);
        }
      } else {
        setArtifactTypeName(undefined);
      }
      // Reset files when navigating at the projectName / typeName levels
      // Also sets the file path within a run or version path within artifacts
      if (pathParts.length < 3) {
        setFiles([]);
        setFolders([]);
        setPath(undefined);
        dispatch({
          type: 'filter',
          payload: [],
        });
      } else {
        setPath(pathParts.slice(3).join('/'));
      }
    }, [pathParts, dispatch]);

    useEffect(() => {
      setArtifactCollectionID(
        data?.entity?.storageTree?.projects.edges
          .find(p => p.node.name === pathParts[0])
          ?.node.artifactSequences.edges.find(a => a.node.name === pathParts[3])
          ?.node.artifactSequenceID
      );
    }, [data?.entity?.storageTree?.projects.edges, artifactName, pathParts]);

    const {
      data: artifacts,
      loading: artifactsLoading,
      refetch: refetchArtifacts,
      error: artifactsError,
    } = useArtifactCollectionStorageTreeQuery({
      variables: {
        entityName,
        enableReferenceTracking: isTrackedPath,
        artifactCollectionID: artifactCollectionID ?? '',
      },
      skip: artifactCollectionID == null,
      fetchPolicy: 'cache-first',
    });

    useEffect(() => {
      if (!isStorageTable) {
        // Clear our state when entityName changes or we switch to tracked
        dispatch({type: 'clear'});
      }
    }, [entityName, isTrackedPath, isStorageTable, dispatch]);

    useEffect(() => {
      if (projectName != null && runName != null) {
        getRunFiles({
          variables: {
            entityName,
            projectName,
            runName,
            maxNumFiles: MAX_NUM_FILES,
            folderPath: runFileFolderPath,
          },
        });
      }
    }, [entityName, runFileFolderPath, projectName, runName, getRunFiles]);

    // Propagate a runs files
    useEffect(() => {
      if (runData != null && runName != null) {
        setFiles(runData.project?.run?.files?.edges.map(e => e.node!) ?? []);
        setFolders(
          runData.project?.run?.folders?.folderList.map(f => f!) ?? []
        );
      }
    }, [runData, runName]);

    // This transforms the storageTree data into something more friendly for us to render
    const rawData = useMemo(() => {
      let root: TreeNode = {
        name: 'root',
        size: 0,
        type: 'entity',
        children: [],
      };

      if (data == null || data.entity == null) {
        return root;
      }
      const {storageTree} = data.entity;
      if (storageTree == null) {
        return root;
      }
      setUsedBytes(storageTree.size);

      root = {
        id: data.entity.id,
        size: storageTree.size,
        type: 'entity',
        name: storageTree.name,
        children: storageTree.projects.edges
          .map(projectEdge => {
            const {
              projectID,
              size,
              name,
              runs,
              artifactSequences,
              artifactTypes,
            } = projectEdge.node;
            let r: TreeNode[] = [];
            if (runs != null) {
              r = runs.edges.map(runEdge => {
                const {runID, size, name} = runEdge.node;

                return {
                  id: runID,
                  name,
                  size,
                  type: 'run',
                  children: [],
                } as TreeNode;
              });
            }
            const artifactTypeSizes = new Map<string, number>();

            for (const artifactType of artifactTypes) {
              artifactTypeSizes.set(artifactType.name, artifactType.size);
            }

            const artifactTypeNodes: TreeNode[] = [];
            artifactSequences.edges.forEach(sequenceEdge => {
              const {artifactSequenceID, size, name, artifactType} =
                sequenceEdge.node;

              // Ignore V1 artifacts or reference only artifacts in storage mode
              if (size !== 0) {
                let at = artifactTypeNodes.find(
                  at => at.name === artifactType.name
                );
                if (at == null) {
                  at = {
                    name: artifactType.name,
                    size: artifactTypeSizes.has(artifactType.name)
                      ? artifactTypeSizes.get(artifactType.name)
                      : 0,
                    type: 'artifact_type',
                    children: [],
                  } as TreeNode;
                  artifactTypeNodes.push(at);
                }

                let av: TreeNode[] = [];
                if (
                  artifactCollectionID != null &&
                  artifactSequenceID === artifactCollectionID
                ) {
                  av =
                    artifacts?.entity?.artifactCollectionStorageTree?.edges.map(
                      artifactEdge => {
                        return {
                          id: artifactEdge.node.artifactID,
                          name: `${name}:${artifactEdge.node.name}`,
                          size: artifactEdge.node.size,
                          type: 'artifact_version',
                          children: [],
                        } as TreeNode;
                      }
                    ) ?? [];
                }

                at.children.push({
                  id: artifactSequenceID,
                  name,
                  size,
                  type: 'artifact',
                  children: av,
                });
              }
            });

            return {
              id: projectID,
              name,
              size,
              type: 'project',
              children: r.concat(artifactTypeNodes),
            } as TreeNode;
          })
          .filter(p => p.size > 0),
      };
      return root;
    }, [data, artifacts, artifactCollectionID]);

    // Used for building the storage tree with selection properly set from state
    const newNodeOrUpdate = useCallback(
      (
        root: Node,
        rawNode: TreeNode,
        subDir: string,
        selectable: boolean = true,
        disabled: boolean = false
      ) => {
        const childSelected = Object.values(state.leafMap).some(
          v =>
            v.path.indexOf(root.name || '') > -1 && v.path.indexOf(subDir) > -1
        );
        const selected =
          rawNode.id != null && state.selectedIds.has(rawNode.id);
        let subNode = nodeFactory({
          disabled,
          selected,
          selectable,
          childSelected,
          id: rawNode.id,
          type: rawNode.type,
          size: rawNode.size,
          name: subDir,
        });
        if (root.subdirectories[subDir]) {
          subNode = root.subdirectories[subDir];
          subNode.id = rawNode.id;
          subNode.size! += rawNode.size!;
          subNode.selected = selected;
        } else {
          root.subdirectories[subDir] = subNode;
        }
        return subNode;
      },
      [state.selectedIds, state.leafMap]
    );

    // Generate a summary of folder contents
    const folderSummary = (root: Node) => {
      let summary = '';
      if (root.fileCount != null && root.fileCount > 0) {
        summary += pluralize('file', root.fileCount);
      }
      if (root.subdirectories.runs || root.name === 'runs') {
        const base =
          root.name === 'runs'
            ? root.subdirectories
            : root.subdirectories.runs.subdirectories;
        summary += pluralize('run', Object.keys(base).length);
      }
      if (root.subdirectories.artifacts || root.type === 'artifact_type') {
        summary +=
          root.type === 'artifact_type' ? '' : summary === '' ? '' : ', ';
        const base =
          root.type === 'artifact_type'
            ? root.subdirectories
            : root.subdirectories.artifacts.subdirectories;
        let artCount = Object.keys(base).length;
        if (root.name === 'artifacts') {
          artCount = Object.keys(base).reduce(
            (i, k) => i + Object.keys(base[k].subdirectories).length,
            0
          );
        }
        summary += pluralize('artifact', artCount);
      }
      return summary;
    };

    const makeArtifactTree = useCallback(
      (
        root: TreeNode,
        projectID: string | undefined,
        artifactType: Node,
        projectSelected: boolean
      ) => {
        const art = newNodeOrUpdate(
          artifactType,
          root,
          root.name,
          true,
          projectSelected
        );
        art.files = root.children.map(av => ({
          id: av.id!,
          name: av.name,
          sizeBytes: av.size,
          disabled: art.selected || art.disabled,
          selected: art.selected || state.selectedIds.has(av.id!),
          artifact: {id: av.id!, digest: av.id!}, // TODO: HAX
        }));

        art.summary = folderSummary(art);

        const totalSize = data?.entity?.storageTree?.projects.edges
          .find(p => p.node.projectID === projectID)
          ?.node.artifactSequences.edges.find(
            a =>
              a.node.artifactType.name === artifactType.name &&
              a.node.name === root.name
          )?.node.size;
        if (totalSize !== undefined) {
          art.size = totalSize;
        }

        return art;
      },
      [newNodeOrUpdate, data, state.selectedIds]
    );

    // Build the storage tree from the backend data and selection state
    const tree = useMemo(() => {
      // TODO: only build as much of the tree as we need?
      const augmented: Node = {
        files: [],
        searchable: true,
        selectable: false,
        subdirectories: {},
        fileCount: 0,
        disabled: teamIsChecked,
      };
      if (rawData == null) {
        return augmented;
      }
      rawData.children.forEach(proj => {
        // Project
        const projectSelected = state.selectedIds.has(proj.id!);
        const filtered =
          state.filtered.length === 0 || state.filtered.indexOf(proj.id!) > -1;
        if (!filtered) {
          return;
        }
        const childSelected = Object.values(state.leafMap).some(
          v => v.path[0] === proj.name
        );
        // don't allow selection in runs / artifacts
        const projNode: Node = nodeFactory({
          selectable: projectName == null,
          childSelected,
          selected: projectSelected,
          size: proj.size,
          id: proj.id,
          type: 'project',
          name: proj.name,
          disabled: teamIsChecked,
        });

        proj.children.forEach(r => {
          const pluralType = r.type === 'artifact_type' ? 'artifacts' : 'runs';
          // Asset type (runs, artifacts)
          const subNode = newNodeOrUpdate(
            projNode,
            r,
            pluralType,
            false,
            projectSelected
          );
          if (r.type === 'artifact_type') {
            // Artifacts
            const artifactType = newNodeOrUpdate(
              subNode,
              r,
              r.name,
              false,
              projectSelected
            );
            r.children.forEach(c =>
              makeArtifactTree(c, proj.id, artifactType, projectSelected)
            );

            artifactType.summary = folderSummary(artifactType);

            const artifactTotalSize =
              data?.entity?.storageTree?.projects.edges.find(
                p => p.node.projectID === proj.id
              )?.node.artifactSequences.totalSize;
            if (artifactTotalSize != null) {
              subNode.size = artifactTotalSize;
            }
          } else {
            // Run
            newNodeOrUpdate(subNode, r, r.name, true, projectSelected);

            const totalSize = data?.entity?.storageTree?.projects.edges.find(
              p => p.node.projectID === proj.id
            )?.node.runs.totalSize;
            if (totalSize != null) {
              subNode.size = totalSize;
            }
          }
          subNode.summary = folderSummary(subNode);
        });
        projNode.summary = folderSummary(projNode);
        augmented.subdirectories[proj.name] = projNode;
      });
      return augmented;
    }, [
      data,
      rawData,
      projectName,
      state.filtered,
      state.selectedIds,
      state.leafMap,
      newNodeOrUpdate,
      makeArtifactTree,
      teamIsChecked,
    ]);

    const projectFolder =
      projectName != null && tree
        ? tree.subdirectories[projectName]
        : undefined;
    const runId =
      (runName &&
        projectFolder &&
        projectFolder.subdirectories.runs?.subdirectories[runName].id) ||
      'null';

    // Summarize what's currently selected
    const selectionStatus = useMemo(() => {
      if (state.selectedIds && state.selectedIds.size > 0) {
        return getSelectionSummary(state);
      } else {
        let summary = `Open a ${projectName ? 'folder' : 'project'} to select ${
          isTrackedPath ? 'artifacts' : 'files'
        } to delete`;
        if (typeName) {
          if (runName) {
            if (path) {
              summary = `Select all files in this folder`;
            } else {
              summary = `Select this run and all files in it`;
            }
          } else if (artifactName) {
            summary = `Select this artifact and all versions in it`;
          } else {
            if (typeName === 'artifacts') {
              summary = 'Open an artifact to delete specific versions';
            } else {
              summary = `Select all ${typeName} in this project`;
            }
          }
        }
        return summary;
      }
    }, [
      projectName,
      artifactName,
      runName,
      typeName,
      isTrackedPath,
      path,
      state,
    ]);

    // Set the root to the augmented storageTree or a runs files
    const currentFolder: Node | undefined = useMemo(() => {
      if (searchResults) {
        return searchResults;
      } else if (
        (files.length > 0 || folders.length > 0) &&
        projectFolder?.id &&
        projectName &&
        runName
      ) {
        // Find selected paths for file folder selection
        const selectedPaths = new Set<string>();
        Object.values(state.leafMap)
          .filter(l => l.path[0] === projectName && l.path[2] === runName)
          .forEach(l => {
            selectedPaths.add(l.path.slice(3).join('/'));
          });
        const selected =
          state.selectedIds.has(runId) ||
          state.selectedIds.has(projectFolder.id);
        return makeFileTree(files, folders, runFileFolderPath, {
          selectedIds: state.selectedIds,
          selectedPaths: Array.from(selectedPaths),
          runId,
          selected,
        });
      } else if (projectFolder && artifactTypeName) {
        if (artifactName) {
          return projectFolder?.subdirectories?.artifacts?.subdirectories?.[
            artifactTypeName
          ]?.subdirectories?.[artifactName];
        } else {
          return projectFolder?.subdirectories?.artifacts?.subdirectories?.[
            artifactTypeName
          ];
        }
      } else {
        return projectFolder || tree;
      }
    }, [
      searchResults,
      files,
      folders,
      state.selectedIds,
      state.leafMap,
      projectFolder,
      projectName,
      artifactName,
      artifactTypeName,
      runName,
      runId,
      tree,
      runFileFolderPath,
    ]);

    // This currently just filters project names, but it's designed to support other
    // types eventually
    function handleSearchChange(e: React.MouseEvent, search: SearchProps) {
      setQuery(search.value);
    }

    const usageCards = useMemo(() => {
      return isComputeHourPath ? (
        <div className="mb-24 w-[16rem]">
          <TrackedHourUsageCard
            resourceType={LimitedResourceType.CUMULATIVE_TRACKED_HOUR}
            loading={loading}
            trackedHourUsageSeconds={computeHours}
          />
        </div>
      ) : (
        <div className="mb-24 w-[16rem]">
          <UsageCardWithCachedView
            loading={loading}
            resource={isTrackedPath ? 'Artifact tracking' : 'Storage'}
            usage={usedBytes}
          />
        </div>
      );
    }, [loading, isComputeHourPath, isTrackedPath, computeHours, usedBytes]);

    const {canSeeOrgInURL} = useContext(AccountSelectorContext);
    if (!canSeeOrgInURL && !envIsLocal) {
      return <NoAccessPage />;
    }

    const Manager = (
      <Tab.Pane as={'div'}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              {showStoragePercentage ? (
                usageCards
              ) : !loading ? (
                <div>
                  Personal usage plan:{' '}
                  {new TimeDelta(computeHours).toHoursString()}
                </div>
              ) : null}
              {isComputeHourPath && (
                <S.InfoBlurb variant="info">
                  <BannerFlexWrapper>
                    <IconLightbulbInfo />
                    <BannerBoldText>Tracked hours</BannerBoldText>
                    <span>
                      is the total time W&B is actively logging a run or
                      experiment.
                    </span>
                  </BannerFlexWrapper>
                </S.InfoBlurb>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {isComputeHourPath ? (
          <ProjectsTable entityName={entityName} />
        ) : (
          <StorageFileBrowser
            entityName={entityName}
            root={currentFolder}
            selectionStatus={selectionStatus}
            deleteButton={
              <StorageConfirmDelete
                onDelete={() => {
                  setFilePath([]);
                  dispatch({type: 'clear'});
                  return refetch({
                    entityName,
                    enableReferenceTracking: isTrackedPath === true,
                  });
                }}
                summary={selectionStatus.replace(' selected', '')}
                rootTree={tree}
                projectId={runData?.project?.run?.projectId}
              />
            }
            onSearchChange={_.debounce(handleSearchChange, 500, {
              leading: true,
            })}
            resetQuery={resetQuery}
            loadingFiles={runLoading}
            loadingProjects={loading}
            loadingArtifacts={artifactsLoading}
            path={pathParts.join('/')}
            setFilePath={setFilePath}
          />
        )}
      </Tab.Pane>
    );

    const panes = [
      envIsPublicCloud
        ? null
        : {
            menuItem: 'Bytes stored',
            render: () => Manager,
          },
      envIsPublicCloud
        ? null
        : {
            menuItem: 'Bytes tracked',
            render: () => Manager,
          },

      {
        menuItem: 'Tracked hours',
        render: () => Manager,
      },
    ];

    let activeIndex = 0;
    if (isTrackedPath) {
      activeIndex = 1;
    } else if (isComputeHourPath) {
      activeIndex = 2;
    }
    const nonNullPanes = panes.filter(isNotNullOrUndefined);

    return isStorageTable ? (
      <StorageFileBrowser
        entityName={entityName}
        root={currentFolder}
        selectionStatus={selectionStatus}
        deleteButton={
          state.selectedIds.size > 0 ? (
            <StorageConfirmDelete
              onDelete={() => {
                dispatch({type: 'clear'});
                return refetch({
                  entityName,
                  enableReferenceTracking: isTrackedPath === true,
                }).then(result => {
                  if (projectName != null && runName != null) {
                    getRunFiles({
                      variables: {
                        entityName,
                        projectName,
                        runName,
                        maxNumFiles: MAX_NUM_FILES,
                        folderPath: runFileFolderPath,
                      },
                    });
                  }
                  return artifactCollectionID != null
                    ? refetchArtifacts({
                        entityName,
                        enableReferenceTracking: isTrackedPath === true,
                        artifactCollectionID: artifactCollectionID ?? '',
                      })
                    : Promise.resolve(result);
                });
              }}
              summary={selectionStatus.replace(' selected', '')}
              rootTree={tree}
              isStorageTable={isStorageTable}
              projectId={runData?.project?.run?.projectId}
            />
          ) : undefined
        }
        onSearchChange={_.debounce(handleSearchChange, 500, {
          leading: true,
        })}
        resetQuery={resetQuery}
        loadingFiles={runLoading}
        loadingProjects={loading}
        loadingArtifacts={artifactsLoading}
        path={pathParts.join('/')}
        setFilePath={setFilePath}
        isStorageTable={isStorageTable}
        orgName={orgName}
        hasLoadingError={
          runError != null || artifactsError != null || storageTreeError != null
        }
      />
    ) : (
      <Tailwind>
        <Container className="mt-24">
          {!userHasPermissions && !loading ? (
            <NoAccessPage />
          ) : (
            <div>
              <h2>
                Manage usage for {orgName && `${orgName} / `}
                {entityName}
              </h2>
              {nonNullPanes.length === 0 ? null : nonNullPanes.length === 1 ? (
                nonNullPanes[0].render()
              ) : (
                <S.StorageExplorerTab
                  activeIndex={activeIndex}
                  onTabChange={(e: any, d: {activeIndex: number}) =>
                    history.replace(
                      Urls.storageFilePath(
                        orgName,
                        entityName,
                        '',
                        false,
                        d.activeIndex === 1,
                        d.activeIndex === 2
                      )
                    )
                  }
                  menu={{secondary: true, pointing: true}}
                  panes={panes}
                />
              )}
            </div>
          )}
        </Container>
      </Tailwind>
    );
  }
);

const StorageExplorerWrapper = (props: StorageExplorerProps) => {
  return (
    <StorageTableContextProvider>
      <StorageExplorer {...props} />
    </StorageTableContextProvider>
  );
};
export default StorageExplorerWrapper;
